import { FC, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import routesConfig from '../../../routeConfig'
import { useAppSelector } from '../../../store/hooks'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'

function parseRouteIcon(icon: string | undefined): IconProp | null {
    if (!icon || typeof icon !== 'string') return null
    const parts = icon.trim().split(/\s+/)
    if (parts.length < 2) return icon as IconProp
    const prefixMap: Record<string, string> = {
        'fa-solid': 'fas',
        'fa-regular': 'far',
        'fa-brands': 'fab',
        'fa-light': 'fal',
    }
    const prefix = prefixMap[parts[0]] || 'fas'
    const name = parts[1].replace(/^fa-/, '')
    return [prefix as 'fas' | 'far' | 'fab' | 'fal', name] as IconProp
}

type RouteConfigItem = (typeof routesConfig)[number] & { breadcrumb?: string }

function pathToSegments(pathname: string): string[] {
    return pathname.split('/').filter(Boolean)
}

function matchRoute(pathPrefix: string, route: RouteConfigItem): boolean {
    const escaped = route.path
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/:[^/]+/g, '[^/]+')
    const regex = new RegExp(`^${escaped}$`)
    return regex.test(pathPrefix)
}

type ModuleLike = { code: string; name: string }

function getBreadcrumbLabel(
    route: RouteConfigItem | undefined,
    segment: string,
    segments: string[],
    segmentIndex: number,
    availableModules: ModuleLike[] = []
): string {
    if (route?.breadcrumb) return route.breadcrumb
    if (segment === 'new') return 'New'
    if (segment === 'view') return 'View'
    // Dynamic module name: /client/modules/:module — use module.name to match page header
    if (
        segments[0] === 'client' &&
        segments[1] === 'modules' &&
        segmentIndex === 2 &&
        availableModules.length > 0
    ) {
        const mod = availableModules.find((m) => m.code === segment)
        if (mod?.name) return mod.name
    }
    return segment.charAt(0).toUpperCase() + segment.slice(1)
}

const DASHBOARD_ROUTE = routesConfig.find(
    (r) => r.path === '/'
) as RouteConfigItem | undefined

function useBreadcrumbsFromConfig(availableModules: ModuleLike[] = []) {
    const location = useLocation()
    return useMemo(() => {
        const pathname = location.pathname
        if (pathname === '/') return []
        const segments = pathToSegments(pathname)
        const items: { pathname: string; breadcrumb: React.ReactNode; route?: RouteConfigItem; isLast: boolean }[] = []
        for (let i = 0; i < segments.length; i++) {
            const pathPrefix = '/' + segments.slice(0, i + 1).join('/')
            const matchingRoutes = routesConfig
                .filter((r): r is RouteConfigItem => !!r.path && matchRoute(pathPrefix, r as RouteConfigItem))
            const route = matchingRoutes.length
                ? matchingRoutes.sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0]
                : undefined
            const label = getBreadcrumbLabel(route, segments[i], segments, i, availableModules)
            items.push({
                pathname: pathPrefix,
                breadcrumb: label,
                route: route as RouteConfigItem | undefined,
                isLast: i === segments.length - 1,
            })
        }
        const dashboardItem = {
            pathname: '/',
            breadcrumb: DASHBOARD_ROUTE?.breadcrumb ?? 'Dashboard',
            route: DASHBOARD_ROUTE,
            isLast: false,
        }
        return [dashboardItem, ...items]
    }, [location.pathname, availableModules])
}

const Breadcrumbs: FC = () => {
    const { pathname } = useLocation()
    const initData = useAppSelector((state: any) => state.security.initData)
    const availableModules = initData?.available_modules ?? []
    const breadcrumbs = useBreadcrumbsFromConfig(availableModules)

    if (pathname === '/' || breadcrumbs.length === 0) return null

    return (
        <nav
            className="app-breadcrumb"
            aria-label="Breadcrumb"
        >
            <ol className="app-breadcrumb__list">
                {breadcrumbs.map(({ pathname: itemPath, breadcrumb, route, isLast }, i) => {
                    const isStatic = route?.static === true
                    const isClickable = !isStatic && !isLast
                    const iconProp = route?.icon ? parseRouteIcon(String(route.icon)) : null
                    const icon = iconProp ? (
                        <FontAwesomeIcon icon={iconProp} className="app-breadcrumb__icon" aria-hidden />
                    ) : null

                    return (
                        <li key={itemPath} className="app-breadcrumb__item">
                            {i > 0 && (
                                <span className="app-breadcrumb__sep" aria-hidden>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </span>
                            )}
                            {isClickable ? (
                                <Link
                                    to={itemPath}
                                    className="app-breadcrumb__link"
                                >
                                    {icon}
                                    <span className="app-breadcrumb__text">{breadcrumb}</span>
                                </Link>
                            ) : (
                                <span
                                    className={`app-breadcrumb__crumb ${isLast ? 'app-breadcrumb__crumb--current' : ''}`}
                                    aria-current={isLast ? 'page' : undefined}
                                >
                                    {icon}
                                    <span className="app-breadcrumb__text">{breadcrumb}</span>
                                </span>
                            )}
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}

export default Breadcrumbs
